/**
 * @file shop-fonts-context.ts
 * @description Контекст своих шрифтов магазина (phase19.16 Р1-7; SB99):
 * список с сервера, реестр шрифтов (каталог платформы + свои) и действия —
 * загрузка, догрузка начертания, переименование, удаление.
 *
 * Один источник правды на все места кабинета, где ключ шрифта превращается
 * в название, образец или сообщение канве: вкладка «Шрифты», окно каталога,
 * карточки ролей, предпросмотр. Значение кладёт `SiteViewLayout`
 * (`useShopFontsState`); вне каркаса — пустое значение: только шрифты
 * платформы, действий нет (тесты и экраны без вида сайта не падают).
 */

import { PLATFORM_FONT_REGISTRY, SITE_LIMITS, type SiteFontRegistry } from "@chaiberi/shared";
import { createContext, useContext } from "react";

import type { AddShopFontFileInput, CreateShopFontInput } from "../../lib/api-site-view";
import type { FontInspectResult, ShopFontDeleteResult, ShopFontDto, ShopFontUploadResult } from "../../lib/site-view/shop-fonts";

export interface ShopFontsApi {
  /** Свои шрифты магазина (порядок — как отдал сервер). */
  items: ShopFontDto[];
  /** Список получен с сервера хотя бы раз. */
  loaded: boolean;
  error: string | null;
  /** Предел семейств на магазин (SITE_LIMITS.shopFonts). */
  limit: number;
  /** Есть право настроек магазина — можно загружать и удалять. */
  canManage: boolean;
  /** Каталог платформы + свои шрифты — для названий, образцов и канвы. */
  registry: SiteFontRegistry;
  reload: () => Promise<void>;
  inspect: (file: File) => Promise<FontInspectResult>;
  create: (input: CreateShopFontInput) => Promise<ShopFontUploadResult>;
  addFile: (id: string, input: AddShopFontFileInput) => Promise<ShopFontUploadResult>;
  rename: (id: string, family: string) => Promise<void>;
  remove: (id: string) => Promise<ShopFontDeleteResult>;
}

const unavailable = (): Promise<never> => Promise.reject(new Error("Свои шрифты недоступны вне конструктора сайта"));

/** Значение вне каркаса: только шрифты платформы. */
export const EMPTY_SHOP_FONTS: ShopFontsApi = {
  items: [],
  loaded: false,
  error: null,
  limit: SITE_LIMITS.shopFonts,
  canManage: false,
  registry: PLATFORM_FONT_REGISTRY,
  reload: () => Promise.resolve(),
  inspect: unavailable,
  create: unavailable,
  addFile: unavailable,
  rename: unavailable,
  remove: unavailable,
};

export const ShopFontsContext = createContext<ShopFontsApi>(EMPTY_SHOP_FONTS);

/** Свои шрифты магазина и реестр шрифтов. */
export function useShopFonts(): ShopFontsApi {
  return useContext(ShopFontsContext);
}

/** Реестр для сообщений канве: только когда список своих шрифтов получен, иначе `null`. */
export function useCanvasFontRegistry(): SiteFontRegistry | null {
  const { loaded, registry } = useContext(ShopFontsContext);
  return loaded ? registry : null;
}
